import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faEye,
    faSearch,
    faRefresh,
    faIndustry,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faPrint,
    faDownload,
    faCalendar,
    faTimesCircle,
    faUser,
    faPhone,
    faMapMarkerAlt,
    faBuilding,
    faBox,
    faDollarSign,
    faMoneyBillWave,
    faReceipt,
    faHistory,
    faCalculator,
    faInfoCircle,
    faHashtag,
    faTag,
    faClipboardList,
    faFileInvoice,
    faClock,
    faChartLine,
    faWarehouse,
    faStore,
    faExchangeAlt,
    faUndo,
    faPercentage,
    faBarcode
} from '@fortawesome/free-solid-svg-icons';
// import "../styles/UsedProductsList.css";

// Create translator instance
const createUsedProductsListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_used_products_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Used Products List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_used_products_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        usedProductsList: 'Used Products List',
        rawMaterialsUsage: 'Raw Materials Usage',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        searchPlaceholder: 'Search usage records...',
        refresh: 'Refresh',
        loadingRecords: 'Loading usage records...',
        noRecordsFound: 'No usage records found',
        recordDetails: 'Usage Details',
        close: 'Close',
        serialNo: 'S.No',
        date: 'Date',
        usageDate: 'Usage Date',
        productName: 'Product',
        batchNumber: 'Batch',
        quantityUsed: 'Quantity',
        unit: 'Unit',
        totalCost: 'Total Cost',
        referenceNo: 'Reference No',
        productionOrderNo: 'Production Order',
        jobNo: 'Job No',
        department: 'Department',
        usedBy: 'Used By',
        actions: 'Actions',
        view: 'View',
        delete: 'Delete',
        deletedSuccess: 'Record deleted successfully',
        deleteError: 'Error deleting record',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        recordId: 'Record ID',
        productDetails: 'Product Details',
        stockInfo: 'Stock Information',
        quantityBefore: 'Quantity Before',
        quantityAfter: 'Quantity After',
        costPerUnit: 'Cost per Unit',
        purpose: 'Purpose',
        remarks: 'Remarks',
        showing: 'Showing',
        of: 'of',
        records: 'records',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        filterBy: 'Filter By',
        productFilter: 'Product Name',
        batchFilter: 'Batch Number',
        referenceFilter: 'Reference No',
        productionOrderFilter: 'Production Order',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        departmentFilter: 'Department',
        allDepartments: 'All Departments',
        production: 'Production',
        cutting: 'Cutting',
        stitching: 'Stitching',
        finishing: 'Finishing',
        quality: 'Quality Control',
        other: 'Other',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this usage record?',
        deleteWarning: 'This will restore the quantity back to stock.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        totalRecords: 'Total Records',
        totalQuantityUsed: 'Total Quantity Used',
        totalCostValue: 'Total Cost Value',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        selectShop: 'Please select a shop to view usage records',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        exportCSV: 'Export CSV',
        printReport: 'Print Report',
        todayUsage: "Today's Usage",
        thisWeekUsage: 'This Week Usage',
        thisMonthUsage: 'This Month Usage',
        mostUsedProduct: 'Most Used Product',
        resetFilters: 'Reset Filters',
        applyFilters: 'Apply Filters'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const usedProductsListTranslator = createUsedProductsListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    const handleShopSelect = (shop) => {
        onSelectShop(shop);
        onClose();
    };

    return (
        <div className="modal-overlay-modern">
            <div className="modal-content-modern shop-selector-modal">
                <div className="modal-header-modern">
                    <div className="modal-icon">
                        <FontAwesomeIcon icon={faStore} />
                    </div>
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="close-btn-modern" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body-modern">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => handleShopSelect(shop)}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const UsedProductsList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr'
    });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Language and theme
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#8b5cf6';
    });
    const [translations, setTranslations] = useState({});

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [filterBy, setFilterBy] = useState('all');
    const [productFilter, setProductFilter] = useState('');
    const [batchFilter, setBatchFilter] = useState('');
    const [referenceFilter, setReferenceFilter] = useState('');
    const [productionOrderFilter, setProductionOrderFilter] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Summary stats
    const [usageSummary, setUsageSummary] = useState(null);

    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Show toast function
    const showToast = (type, message) => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction
        });

        setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
        }, 3000);
    };

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                document.documentElement.style.setProperty('--used-products-primary-color', color);
                document.documentElement.style.setProperty('--used-products-primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--used-products-primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(currentTheme);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            setCurrentTheme(theme);
            applyTheme(theme);
        };

        window.addEventListener('storage', handleThemeChange);
        return () => window.removeEventListener('storage', handleThemeChange);
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await usedProductsListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
    }, [currentLanguage]);

    // Fetch records when shop changes
    useEffect(() => {
        if (currentShop) {
            fetchRecords();
            fetchUsageSummary();
            fetchAvailableShops();
        } else {
            setLoading(false);
        }
    }, [currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [records, searchTerm, filterBy, productFilter, batchFilter, referenceFilter, productionOrderFilter, departmentFilter, startDate, endDate]);

    // Fetch available shops
    const fetchAvailableShops = async () => {
        if (!user) return;

        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(
                shop.shop_id,
                shop.shop_name,
                shop.shop_code
            );

            if (success) {
                showToast('success', translations.shopSwitched || 'Shop switched successfully');
            } else {
                showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', translations.shopSwitchFailed || 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="btn-modern btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="shop-indicator-modern active">
                <div className="shop-badge">
                    <FontAwesomeIcon icon={faStore} />
                    <div className="shop-details">
                        <span className="shop-name">{currentShop.shop_name}</span>
                        <span className="shop-code">({currentShop.shop_code || currentShop.shop_id})</span>
                    </div>
                </div>
                <div className="shop-info-note">
                    {translations.rawMaterialsUsage || 'Showing raw material usage from this shop'}
                </div>
                <button
                    className="btn-modern btn-switch-shop"
                    onClick={() => setShowShopSelector(true)}
                >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span>{translations.switchShop || 'Switch Shop'}</span>
                </button>
            </div>
        );
    };

    const fetchRecords = async () => {
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_used_records',
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                setRecords(data.data || []);
                setSummary(data.summary || null);
                
                if (data.data.length === 0) {
                    showToast('info', translations.noRecordsFound || 'No usage records found');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching records:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsageSummary = async () => {
        if (!currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_usage_summary',
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setUsageSummary(data.data);
            }
        } catch (error) {
            console.error('Error fetching usage summary:', error);
        }
    };

    const applyFilters = () => {
        let filtered = [...records];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(record =>
                record.product_name?.toLowerCase().includes(term) ||
                record.batch_no?.toLowerCase().includes(term) ||
                record.reference_no?.toLowerCase().includes(term) ||
                record.production_order_no?.toLowerCase().includes(term) ||
                record.used_by?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'product' && productFilter.trim()) {
            const productTerm = productFilter.toLowerCase();
            filtered = filtered.filter(record =>
                record.product_name?.toLowerCase().includes(productTerm)
            );
        }

        if (filterBy === 'batch' && batchFilter.trim()) {
            const batchTerm = batchFilter.toLowerCase();
            filtered = filtered.filter(record =>
                record.batch_no?.toLowerCase().includes(batchTerm)
            );
        }

        if (filterBy === 'reference' && referenceFilter.trim()) {
            const refTerm = referenceFilter.toLowerCase();
            filtered = filtered.filter(record =>
                record.reference_no?.toLowerCase().includes(refTerm)
            );
        }

        if (filterBy === 'production_order' && productionOrderFilter.trim()) {
            const poTerm = productionOrderFilter.toLowerCase();
            filtered = filtered.filter(record =>
                record.production_order_no?.toLowerCase().includes(poTerm)
            );
        }

        if (departmentFilter && departmentFilter !== 'all') {
            filtered = filtered.filter(record => record.department === departmentFilter);
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(record => {
                if (!record.date) return false;
                const recordDate = new Date(record.date);
                recordDate.setHours(0, 0, 0, 0);
                return recordDate >= start;
            });
        }

        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(record => {
                if (!record.date) return false;
                const recordDate = new Date(record.date);
                return recordDate <= end;
            });
        }

        setFilteredRecords(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setProductFilter('');
        setBatchFilter('');
        setReferenceFilter('');
        setProductionOrderFilter('');
        setDepartmentFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    };

    const handleView = (record) => {
        setSelectedRecord(record);
        setShowViewModal(true);
    };

    const handleDeleteClick = (recordId) => {
        setRecordToDelete(recordId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!recordToDelete || !currentShop) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/used_products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'delete_used_record',
                    id: recordToDelete,
                    shop_id: currentShop.shop_id
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Record deleted successfully');
                fetchRecords();
                fetchUsageSummary();
                setShowDeleteModal(false);
                setRecordToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting record');
            }
        } catch (error) {
            console.error('Error deleting record:', error);
            showToast('error', translations.deleteError || 'Error deleting record');
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return date.toLocaleString('en-IN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '-';
        return `₹ ${parseFloat(amount).toFixed(2)}`;
    };

    const formatQuantity = (quantity, unit) => {
        if (!quantity && quantity !== 0) return '-';
        return `${parseFloat(quantity).toFixed(3)} ${unit || ''}`;
    };

    const getDepartmentBadgeClass = (department) => {
        switch (department) {
            case 'production': return 'department-production';
            case 'cutting': return 'department-cutting';
            case 'stitching': return 'department-stitching';
            case 'finishing': return 'department-finishing';
            case 'quality': return 'department-quality';
            default: return 'department-other';
        }
    };

    const getDepartmentLabel = (department) => {
        const labels = {
            production: translations.production || 'Production',
            cutting: translations.cutting || 'Cutting',
            stitching: translations.stitching || 'Stitching',
            finishing: translations.finishing || 'Finishing',
            quality: translations.quality || 'Quality Control',
            other: translations.other || 'Other'
        };
        return labels[department] || department || '-';
    };

    const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentRecords = filteredRecords.slice(startIndex, endIndex);

    // Calculate totals from filtered records
    const totalFilteredQuantity = filteredRecords.reduce((sum, record) => sum + (parseFloat(record.quantity_used) || 0), 0);
    const totalFilteredCost = filteredRecords.reduce((sum, record) => sum + (parseFloat(record.total_cost) || 0), 0);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const getTodayDate = () => new Date().toISOString().split('T')[0];

    const departments = [
        { value: 'all', label: translations.allDepartments || 'All Departments' },
        { value: 'production', label: translations.production || 'Production' },
        { value: 'cutting', label: translations.cutting || 'Cutting' },
        { value: 'stitching', label: translations.stitching || 'Stitching' },
        { value: 'finishing', label: translations.finishing || 'Finishing' },
        { value: 'quality', label: translations.quality || 'Quality Control' },
        { value: 'other', label: translations.other || 'Other' }
    ];

    return (
        <div className={`used-products-list-modern ${isRTL ? 'rtl' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Shop Selector Modal */}
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faIndustry} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.usedProductsList || 'Used Products List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : translations.rawMaterialsUsage || 'Raw Materials Usage'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Shop Indicator */}
                {renderShopIndicator()}
            </div>

            {/* Summary Stats */}
            {usageSummary && (
                <div className="summary-stats-modern">
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
                            <FontAwesomeIcon icon={faHistory} style={{ color: '#8b5cf6' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{usageSummary.today?.total_used || 0}</div>
                            <div className="stat-label">{translations.todayUsage || "Today's Usage"}</div>
                            <div className="stat-sub">{formatQuantity(usageSummary.today?.total_quantity || 0, 'units')}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#10b981' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{usageSummary.this_month?.total_used || 0}</div>
                            <div className="stat-label">{translations.thisMonthUsage || 'This Month Usage'}</div>
                            <div className="stat-sub">{formatCurrency(usageSummary.this_month?.total_cost || 0)}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)' }}>
                            <FontAwesomeIcon icon={faChartLine} style={{ color: '#f59e0b' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{summary?.total_used_records || 0}</div>
                            <div className="stat-label">{translations.totalRecords || 'Total Records'}</div>
                            <div className="stat-sub">{formatCurrency(summary?.total_cost || 0)}</div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
                            <FontAwesomeIcon icon={faBox} style={{ color: '#3b82f6' }} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-value">{formatQuantity(totalFilteredQuantity, '')}</div>
                            <div className="stat-label">{translations.totalQuantityUsed || 'Total Quantity'}</div>
                            <div className="stat-sub">{formatCurrency(totalFilteredCost)}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Top Products Section */}
            {usageSummary?.top_products?.length > 0 && (
                <div className="top-products-banner">
                    <div className="banner-icon">
                        <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div className="banner-content">
                        <span className="banner-label">{translations.mostUsedProduct || 'Most Used Product'}:</span>
                        <span className="banner-product">{usageSummary.top_products[0]?.product_name}</span>
                        <span className="banner-stats">
                            {formatQuantity(usageSummary.top_products[0]?.total_used || 0, 'units')} ({usageSummary.top_products[0]?.usage_count || 0} uses)
                        </span>
                    </div>
                </div>
            )}

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    {/* Filter By Dropdown */}
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.filterBy || 'Filter By'}</option>
                            <option value="product">{translations.productFilter || 'Product Name'}</option>
                            <option value="batch">{translations.batchFilter || 'Batch Number'}</option>
                            <option value="reference">{translations.referenceFilter || 'Reference No'}</option>
                            <option value="production_order">{translations.productionOrderFilter || 'Production Order'}</option>
                        </select>
                    </div>

                    {filterBy === 'product' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={productFilter}
                                onChange={(e) => setProductFilter(e.target.value)}
                                placeholder={translations.productFilter || 'Product name...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'batch' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={batchFilter}
                                onChange={(e) => setBatchFilter(e.target.value)}
                                placeholder={translations.batchFilter || 'Batch number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'reference' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={referenceFilter}
                                onChange={(e) => setReferenceFilter(e.target.value)}
                                placeholder={translations.referenceFilter || 'Reference number...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {filterBy === 'production_order' && (
                        <div className="filter-group compact">
                            <input
                                type="text"
                                value={productionOrderFilter}
                                onChange={(e) => setProductionOrderFilter(e.target.value)}
                                placeholder={translations.productionOrderFilter || 'Production order...'}
                                className="filter-input compact"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    )}

                    {/* Department Filter */}
                    <div className="filter-group compact">
                        <select
                            value={departmentFilter}
                            onChange={(e) => setDepartmentFilter(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            {departments.map(dept => (
                                <option key={dept.value} value={dept.value}>{dept.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date Range */}
                    <div className="filter-group compact date-filter">
                        <input
                            ref={startDateRef}
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={getTodayDate()}
                            className="filter-input compact date-input"
                            placeholder={translations.startDate || 'Start Date'}
                        />
                    </div>
                    <div className="filter-group compact date-filter">
                        <input
                            ref={endDateRef}
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={getTodayDate()}
                            className="filter-input compact date-input"
                            placeholder={translations.endDate || 'End Date'}
                        />
                    </div>

                    {/* Search Box */}
                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input
                                type="text"
                                placeholder={translations.searchPlaceholder || 'Search usage records...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                        </div>
                    </div>

                    {/* Reset Filters Button */}
                    <div className="filter-group compact">
                        <button
                            onClick={resetFilters}
                            className="reset-btn-modern compact"
                            title={translations.resetFilters || 'Reset Filters'}
                        >
                            <FontAwesomeIcon icon={faTimesCircle} />
                            <span className="btn-text">{translations.resetFilters || 'Reset'}</span>
                        </button>
                    </div>

                    {/* Refresh Button */}
                    <div className="filter-group compact">
                        <button
                            onClick={fetchRecords}
                            className="refresh-btn-modern compact"
                            disabled={loading}
                            title={translations.refresh || 'Refresh'}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingRecords || 'Loading usage records...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view usage records'}</p>
                        <button
                            className="btn-modern btn-primary-modern"
                            onClick={() => setShowShopSelector(true)}
                        >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="centered-cell">{translations.date || 'Date'}</th>
                                            <th className="centered-cell">{translations.productName || 'Product'}</th>
                                            <th className="centered-cell">{translations.batchNumber || 'Batch'}</th>
                                            <th className="centered-cell">{translations.quantityUsed || 'Quantity'}</th>
                                            <th className="centered-cell">{translations.totalCost || 'Total Cost'}</th>
                                            <th className="centered-cell">{translations.referenceNo || 'Ref No'}</th>
                                            <th className="centered-cell">{translations.productionOrderNo || 'PO No'}</th>
                                            <th className="centered-cell">{translations.department || 'Dept'}</th>
                                            <th className="centered-cell">{translations.usedBy || 'Used By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRecords.map((record, index) => (
                                            <tr key={record.id}>
                                                <td className="id-cell centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                        <span className="cell-text">{startIndex + index + 1}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faCalendar} className="cell-icon" />
                                                        <span className="cell-text">{formatDate(record.date)}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faBox} className="cell-icon" />
                                                        <span className="cell-text">{record.product_name}</span>
                                                        {record.production_order_no && (
                                                            <div className="cell-subtext">
                                                                <FontAwesomeIcon icon={faClipboardList} size="xs" />
                                                                <span>{record.production_order_no}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <FontAwesomeIcon icon={faTag} className="cell-icon" />
                                                        <span className="cell-text batch-number">{record.batch_no || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="quantity-badge">
                                                            {parseFloat(record.quantity_used).toFixed(3)} {record.unit}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="cost-amount" style={{ color: '#059669', fontWeight: 'bold' }}>
                                                            {formatCurrency(record.total_cost)}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="reference-text">{record.reference_no || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="po-text">{record.production_order_no || '-'}</span>
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        {record.department && (
                                                            <div className={`department-badge ${getDepartmentBadgeClass(record.department)}`}>
                                                                {getDepartmentLabel(record.department)}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="centered-cell">
                                                    <div className="cell-content centered-content">
                                                        <span className="used-by-badge">
                                                            <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                            <span className="cell-text">{record.used_by}</span>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="actions-cell-modern centered-cell">
                                                    <div className="cell-content centered-content actions-content">
                                                        <button
                                                            onClick={() => handleView(record)}
                                                            className="action-btn-modern view-btn-modern"
                                                            title={translations.view || 'View'}
                                                        >
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(record.id)}
                                                            className="action-btn-modern delete-btn-modern"
                                                            title={translations.delete || 'Delete'}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* No Data Message */}
                                {currentRecords.length === 0 && !loading && currentShop && (
                                    <div className="no-data-modern">
                                        <FontAwesomeIcon icon={faIndustry} />
                                        <p>{translations.noRecordsFound || 'No usage records found'}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredRecords.length > 0 && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredRecords.length)} {translations.of || 'of'} {filteredRecords.length} {translations.records || 'records'}</span>
                                {currentShop?.shop_name && (
                                    <span className="shop-info-pagination">
                                        | {translations.shopName || 'Shop'}: {currentShop.shop_name}
                                    </span>
                                )}
                                <span style={{ marginLeft: '20px', color: '#8b5cf6', fontWeight: '600' }}>
                                    {translations.totalQuantityUsed || 'Total Quantity'}: {formatQuantity(totalFilteredQuantity, '')}
                                </span>
                                <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>
                                    {translations.totalCostValue || 'Total Cost'}: {formatCurrency(totalFilteredCost)}
                                </span>
                            </div>
                            <div className="pagination-controls">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>

                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedRecord && (
                <div className="modal-overlay-modern" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faReceipt} />
                            </div>
                            <div className="modal-title">
                                <h3>{translations.recordDetails || 'Usage Details'}</h3>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            {/* Basic Information */}
                            <div className="detail-section">
                                <h4 className="section-title">{translations.recordInfo || 'Record Information'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.recordId || 'Record ID'}:</span>
                                        <span className="detail-value">#{selectedRecord.id}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.date || 'Date'}:</span>
                                        <span className="detail-value">{formatDate(selectedRecord.date)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.usageDate || 'Usage Date'}:</span>
                                        <span className="detail-value">{formatDateTime(selectedRecord.used_date)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.referenceNo || 'Reference No'}:</span>
                                        <span className="detail-value">{selectedRecord.reference_no || '-'}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.productionOrderNo || 'Production Order'}:</span>
                                        <span className="detail-value">{selectedRecord.production_order_no || '-'}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.jobNo || 'Job No'}:</span>
                                        <span className="detail-value">{selectedRecord.job_no || '-'}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.department || 'Department'}:</span>
                                        <span className="detail-value">
                                            {selectedRecord.department && (
                                                <div className={`department-badge ${getDepartmentBadgeClass(selectedRecord.department)}`}>
                                                    {getDepartmentLabel(selectedRecord.department)}
                                                </div>
                                            )}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.usedBy || 'Used By'}:</span>
                                        <span className="detail-value">
                                            <span className="used-by-badge">
                                                <FontAwesomeIcon icon={faUser} />
                                                {selectedRecord.used_by}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Product Details */}
                            <div className="detail-section">
                                <h4 className="section-title">{translations.productDetails || 'Product Details'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.productName || 'Product Name'}:</span>
                                        <span className="detail-value">{selectedRecord.product_name}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.batchNumber || 'Batch Number'}:</span>
                                        <span className="detail-value batch-number">{selectedRecord.batch_no || '-'}</span>
                                    </div>
                                    {selectedRecord.barcode && (
                                        <div className="detail-row-modern">
                                            <span className="detail-label">{translations.barcode || 'Barcode'}:</span>
                                            <span className="detail-value">{selectedRecord.barcode}</span>
                                        </div>
                                    )}
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.unit || 'Unit'}:</span>
                                        <span className="detail-value">{selectedRecord.unit}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.costPerUnit || 'Cost per Unit'}:</span>
                                        <span className="detail-value">{formatCurrency(selectedRecord.cost_price)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Quantity Information */}
                            <div className="detail-section">
                                <h4 className="section-title">{translations.stockInfo || 'Stock Information'}</h4>
                                <div className="details-grid">
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.quantityBefore || 'Quantity Before'}:</span>
                                        <span className="detail-value">{formatQuantity(selectedRecord.quantity_before, selectedRecord.unit)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.quantityUsed || 'Quantity Used'}:</span>
                                        <span className="detail-value" style={{ color: '#ef4444', fontWeight: 'bold' }}>
                                            -{formatQuantity(selectedRecord.quantity_used, selectedRecord.unit)}
                                        </span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.quantityAfter || 'Quantity After'}:</span>
                                        <span className="detail-value">{formatQuantity(selectedRecord.quantity_after, selectedRecord.unit)}</span>
                                    </div>
                                    <div className="detail-row-modern">
                                        <span className="detail-label">{translations.totalCost || 'Total Cost'}:</span>
                                        <span className="detail-value" style={{ color: '#059669', fontWeight: 'bold' }}>
                                            {formatCurrency(selectedRecord.total_cost)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Additional Information */}
                            {(selectedRecord.purpose || selectedRecord.remarks) && (
                                <div className="detail-section">
                                    <h4 className="section-title">{translations.additionalInfo || 'Additional Information'}</h4>
                                    <div className="details-grid">
                                        {selectedRecord.purpose && (
                                            <div className="detail-row-modern full-width">
                                                <span className="detail-label">{translations.purpose || 'Purpose'}:</span>
                                                <span className="detail-value">{selectedRecord.purpose}</span>
                                            </div>
                                        )}
                                        {selectedRecord.remarks && (
                                            <div className="detail-row-modern full-width">
                                                <span className="detail-label">{translations.remarks || 'Remarks'}:</span>
                                                <span className="detail-value remarks-text">{selectedRecord.remarks}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>
                                {translations.close || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <div className="modal-title">
                                <h3 style={{ color: '#ef4444' }}>{translations.confirmDelete || 'Confirm Delete'}</h3>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowDeleteModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#1f2937' }}>
                                    {translations.deleteMessage || 'Are you sure you want to delete this usage record?'}
                                </p>
                                <p style={{ fontSize: '0.9rem', color: '#f59e0b' }}>
                                    <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                                    {translations.deleteWarning || 'This will restore the quantity back to stock.'}
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button
                                type="button"
                                className="btn-modern btn-secondary-modern"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.noCancel || 'No, Cancel'}
                            </button>
                            <button
                                type="button"
                                className="btn-modern btn-delete-modern"
                                onClick={handleDelete}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                                {translations.yesDelete || 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsedProductsList;